"use client";

import Image from "next/image";
import { articleCoverPath } from "@/helpers/articleCover";
import { Link, useRouter } from "@/i18n/navigation";
import InfoBadge from "@/components/ui/InfoBadge/InfoBadge";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import moment from "moment-timezone";
import { useSearchParams } from "next/navigation";
import Alert from "@/components/ui/Alert/Alert";
import { useLocale, useTranslations } from "next-intl";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { labels } from "@/i18n/routing";
import ArticlesCategoryFilter from "@/components/ArticlesCategoryFilter";
import { useSession } from "next-auth/react";
import BookmarkButton from "@/components/ui/BookmarkButton/BookmarkButton";

/**
 * @param {object} props
 * @param {Array|null} props.initialArticles première page rendue par le serveur,
 *        pour que le HTML contienne déjà les liens vers les articles. null quand
 *        le serveur n'a pas pu les obtenir : on retombe alors sur le chargement
 *        depuis le navigateur, comme avant.
 * @param {number|null} props.initialTotal nombre de pages correspondant.
 */
export default function ArticlesComponent({
    initialArticles = null,
    initialTotal = null,
}) {
    const { data: session } = useSession();
    const locale = useLocale();
    const searchParams = useSearchParams();
    const containerRef = useRef(null);
    const router = useRouter();
    const t = useTranslations();
    const userTimezone = session?.user?.timezone || moment.tz.guess();

    const [articles, setArticles] = useState(initialArticles || []);
    const [selectedArticles, setSelectedArticles] = useState(
        initialArticles || [],
    );
    const [page, setPage] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [numberOfPages, setNumberOfPages] = useState(initialTotal);
    const [updating, setUpdating] = useState(false);
    // La dernière page et la dernière catégorie chargées, pour savoir ce qui vient de changer.
    const lastLoaded = useRef({ page: 1, category: "all" });
    const reduceMotion = useReducedMotion();
    // Le rendu serveur n'exécute aucun effet : sans cet état initial, la page
    // partirait avec le squelette de chargement et pas une seule carte, donc
    // pas un seul lien vers un article dans le HTML.
    const [firstLoadingDone, setFirstLoadingDone] = useState(
        Boolean(initialArticles),
    );
    const [languageChanged, setLanguageChanged] = useState(false);
    const [bookmarkedIds, setBookmarkedIds] = useState(new Set());

    const fetchArticles = async (tmpPage = null, forceCategory = null) => {
        if (updating) return;

        setUpdating(true);

        let selectedPage = 1;
        if (tmpPage) {
            selectedPage = tmpPage;
        } else {
            selectedPage = checkPage();
        }

        // Category - only check URL if no forceCategory provided
        let category = forceCategory || checkCategory();

        const res = await fetch(`/api/articles/post/articles-with-pagination`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                page: selectedPage,
                max: 15,
                categories: [category],
                locale,
            }),
        });

        const data = await res.json();

        setArticles(data.articles);
        setSelectedArticles(data.articles);
        setNumberOfPages(parseInt(data.total));

        // Retour en haut de la liste, seulement quand on change de PAGE : le lecteur est
        // alors en bas, sur la pagination. Au changement de catégorie il est déjà devant
        // les filtres, et ce défilement le faisait glisser de quelques lignes sans raison.
        const pageChanged = lastLoaded.current.category === category && lastLoaded.current.page !== selectedPage;
        lastLoaded.current = { page: selectedPage, category };
        if (firstLoadingDone && pageChanged) {
            containerRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }

        setFirstLoadingDone(true);

        setUpdating(false);
    };
    const checkPage = () => {
        let tmpPage = searchParams.get("page") || 1;
        if (tmpPage != page) {
            setPage(parseInt(tmpPage));
        }
        return parseInt(tmpPage);
    };
    const checkCategory = () => {
        let tmpCategory = searchParams.get("category") || "all";
        setSelectedCategory(tmpCategory);
        return tmpCategory;
    };
    const filterCourses = (category) => {
        if (category === "all") {
            setPage(1);
            setSelectedCategory("all");
            fetchArticles(1, "all");
        } else {
            setPage(1);
            setSelectedCategory(category);
            fetchArticles(1, category);
        }

        // `scroll: false` : sans lui, le routeur fait défiler la page pour ramener le
        // contenu à l'écran à chaque changement de catégorie, d'où un glissement de
        // quelques lignes. (L'ancienne écriture passait ses options en troisième
        // argument, à la manière de l'ancien routeur : elles étaient ignorées.)
        router.push(category === "all" ? `/articles` : `/articles?category=${category}`, { scroll: false });
    };

    useEffect(() => {
        if (!firstLoadingDone) return;
        checkPage();
        fetchArticles();
    }, [searchParams]);

    useEffect(() => {
        // Le serveur ne rend que la première page, catégorie « toutes ». Si
        // l'adresse demande exactement cela, les articles sont déjà là : les
        // redemander ferait un aller-retour réseau pour un résultat identique,
        // et ferait clignoter la liste. Dès que l'URL demande autre chose, on
        // charge normalement.
        const urlPage = parseInt(searchParams.get("page") || 1);
        const urlCategory = searchParams.get("category") || "all";

        if (initialArticles && urlPage === 1 && urlCategory === "all") {
            setPage(1);
            setSelectedCategory("all");
            setFirstLoadingDone(true);
            return;
        }

        checkPage();
        fetchArticles();
    }, []);

    // Fetch bookmarked article IDs once when session is available
    useEffect(() => {
        if (!session) return;

        const fetchBookmarkedIds = async () => {
            try {
                const res = await fetch("/api/article/bookmarked-ids", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                });
                if (res.ok) {
                    const data = await res.json();
                    setBookmarkedIds(new Set(data.articleIds));
                }
            } catch (error) {
                // Silently fail
            }
        };

        fetchBookmarkedIds();
    }, [session]);

    return (
        <>
            {/* Head */}
            <section className="flex gap-10 items-center mb-3">
                {/* Infos */}
                <div>
                    <h1 className="md:text-4xl text-3xl dark:text-white font-bold font-sourceSans pt-5">
                        {t("articles_title")}
                    </h1>
                    <div
                        className="text-lg text-[#05060F]/70 dark:text-textSecondDark mt-4"
                        ref={containerRef}
                    >
                        {t("articles_headline")}
                    </div>
                </div>
            </section>

            {/* Filters */}
            <section className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6 mb-8">
                <ArticlesCategoryFilter
                    category={selectedCategory}
                    onCategoryChange={filterCourses}
                />
                <Select
                    onValueChange={(value) =>
                        window.open(value, "_blank")
                    }
                >
                    <SelectTrigger className="w-[180px]">
                        <SelectValue
                            placeholder={t("articles_feeds_select")}
                        />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-bgDark-840 dark:border-borderDark">
                        {labels.map((label) => (
                            <SelectItem
                                key={label.id}
                                value={`/feeds/articles-${label.id}.xml`}
                            >
                                {label.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </section>

            <hr className="mb-8 dark:border-borderDark" />

            {/* Content */}
            <section className="flex flex-col gap-5">
                    {firstLoadingDone ? (
                        <>
                            {selectedArticles.length != 0 &&
                                languageChanged && (
                                    <>
                                        <Alert>
                                            {t(
                                                "articles_languageNotSupportedYet",
                                            )}
                                        </Alert>
                                    </>
                                )}

                            {selectedArticles.length == 0 && !updating && (
                                <Alert>{t("articles_noArticles")}</Alert>
                            )}

                            {/* Au changement de catégorie ou de page, les cartes ne sont plus
                                remplacées par des cartes grises : elles s'estompent le temps du
                                chargement, puis celles qui restent glissent à leur nouvelle place,
                                les autres passent par un fondu court. Même réglage que sur les
                                formations et les challenges. */}
                            <div
                                className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 transition-opacity duration-200 ${
                                    updating ? "opacity-50 pointer-events-none" : ""
                                }`}
                                aria-busy={updating}
                            >
                                <AnimatePresence mode="popLayout" initial={false}>
                                {selectedArticles.map((article) => (
                                    <motion.div
                                        key={`article-${article.id}`}
                                        layout={!reduceMotion}
                                        initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, transition: { duration: 0.15 } }}
                                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                        className="h-full relative"
                                    >
                                        {session && (
                                            <div className="absolute top-2 right-2 z-20">
                                                <BookmarkButton
                                                    articleId={article.id}
                                                    initialBookmarked={bookmarkedIds.has(article.id)}
                                                    size="small"
                                                />
                                            </div>
                                        )}
                                    <Link
                                        href={`/r/${article.slug}`}
                                        className="h-full"
                                    >
                                        <div className="border border-gray-200 dark:border-borderDark bg-white dark:bg-bgDark-840 rounded-xl overflow-hidden group hover:bg-gray-50 dark:hover:bg-bgDark hover:border-primary dark:hover:border-primary transition-all duration-200 h-full flex flex-col">
                                            {/* Thumbnail */}
                                            <div className="aspect-[16/9] relative">
                                                {/* Sa vignette, ou celle qu'on
                                                    dessine à sa place : un
                                                    article sans couverture
                                                    laissait un aplat de couleur
                                                    à cet endroit. */}
                                                {articleCoverPath(
                                                    article,
                                                    locale,
                                                ) ? (
                                                    <Image
                                                        src={articleCoverPath(
                                                            article,
                                                            locale,
                                                        )}
                                                        alt={article.title}
                                                        fill
                                                        unoptimized={!article.cover}
                                                        className="object-cover"
                                                    />
                                                ) : (
                                                    <div className="bg-primary/10 dark:bg-primary/20 w-full h-full"></div>
                                                )}
                                            </div>

                                            {/* Infos */}
                                            <div className="flex-1 flex flex-col p-5">
                                                <div className="flex-1">
                                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-primary font-sourceSans line-clamp-2">
                                                        {article.title}
                                                    </h3>
                                                    <p className="text-sm text-gray-600 dark:text-grayDark-400 font-geistSans mt-2 line-clamp-3 mb-0">
                                                        {article.description}
                                                    </p>
                                                </div>
                                                <div className="mt-4 flex items-center gap-2">
                                                    <span className="text-xs text-gray-500 dark:text-grayDark-400">
                                                        {moment(
                                                            article.dateCreation,
                                                        )
                                                            .tz(userTimezone)
                                                            .locale(locale)
                                                            .format("LL")}
                                                    </span>
                                                    {languageChanged && (
                                                        <InfoBadge type="red">
                                                            {t(
                                                                "courses_courseNotInYourLanguage",
                                                            )}
                                                        </InfoBadge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                    </motion.div>
                                ))}
                                </AnimatePresence>
                            </div>

                            {selectedArticles.length != 0 && (
                                <div className="flex justify-center items-center pt-5">
                                    <div className="flex gap-2">
                                        {page != 1 && (
                                            <Link
                                                href={`/articles?page=${
                                                    page - 1
                                                }&category=${selectedCategory}`}
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white hover:bg-primary-300 hover:text-white"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-5 h-5"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        fill="currentColor"
                                                        d="m8.165 11.63l6.63-6.43C15.21 4.799 16 5.042 16 5.57v12.86c0 .528-.79.771-1.205.37l-6.63-6.43a.499.499 0 0 1 0-.74Z"
                                                    ></path>
                                                </svg>
                                            </Link>
                                        )}

                                        {/* Show 4 pages before page and 4 page after page */}
                                        {page - 4 > 0 && (
                                            <Link
                                                href={`/articles?page=${
                                                    page - 4
                                                }&category=${selectedCategory}`}
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white hover:bg-primary-300 hover:text-white"
                                            >
                                                {page - 4}
                                            </Link>
                                        )}
                                        {page - 3 > 0 && (
                                            <Link
                                                href={`/articles?page=${
                                                    page - 3
                                                }&category=${selectedCategory}`}
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white hover:bg-primary-300 hover:text-white"
                                            >
                                                {page - 3}
                                            </Link>
                                        )}
                                        {page - 2 > 0 && (
                                            <Link
                                                href={`/articles?page=${
                                                    page - 2
                                                }&category=${selectedCategory}`}
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white hover:bg-primary-300 hover:text-white"
                                            >
                                                {page - 2}
                                            </Link>
                                        )}
                                        {page - 1 > 0 && (
                                            <Link
                                                href={`/articles?page=${
                                                    page - 1
                                                }&category=${selectedCategory}`}
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white hover:bg-primary-300 hover:text-white"
                                            >
                                                {page - 1}
                                            </Link>
                                        )}

                                        {/* Current page */}
                                        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-300 text-white">
                                            {page}
                                        </div>

                                        {page + 1 <= numberOfPages && (
                                            <Link
                                                href={`/articles?page=${
                                                    page + 1
                                                }&category=${selectedCategory}`}
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white hover:bg-primary-300 hover:text-white"
                                            >
                                                {page + 1}
                                            </Link>
                                        )}
                                        {page + 2 <= numberOfPages && (
                                            <Link
                                                href={`/articles?page=${
                                                    page + 2
                                                }&category=${selectedCategory}`}
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white hover:bg-primary-300 hover:text-white"
                                            >
                                                {page + 2}
                                            </Link>
                                        )}
                                        {page + 3 <= numberOfPages && (
                                            <Link
                                                href={`/articles?page=${
                                                    page + 3
                                                }&category=${selectedCategory}`}
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white hover:bg-primary-300 hover:text-white"
                                            >
                                                {page + 3}
                                            </Link>
                                        )}
                                        {page + 4 <= numberOfPages && (
                                            <Link
                                                href={`/articles?page=${
                                                    page + 4
                                                }&category=${selectedCategory}`}
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white hover:bg-primary-300 hover:text-white"
                                            >
                                                {page + 4}
                                            </Link>
                                        )}

                                        {page != numberOfPages && (
                                            <Link
                                                href={`/articles?page=${
                                                    page + 1
                                                }&category=${selectedCategory}`}
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white hover:bg-primary-300 hover:text-white"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-5 h-5"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        fill="currentColor"
                                                        d="M15.835 11.63L9.205 5.2C8.79 4.799 8 5.042 8 5.57v12.86c0 .528.79.771 1.205.37l6.63-6.43a.498.498 0 0 0 0-.74Z"
                                                    ></path>
                                                </svg>
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={`s-${i}`}
                                    className="relative border border-gray-200 dark:border-bgDark-300 bg-white dark:bg-bgDark-840 rounded-xl overflow-hidden h-full flex flex-col"
                                >
                                    <div className="aspect-[16/9] bg-gray-200 dark:bg-bgDark-300 animate-pulse relative">
                                        <div className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-white/90 dark:bg-bgDark-840/90 backdrop-blur-sm flex items-center justify-center">
                                            <div className="w-4 h-4 rounded bg-gray-300 dark:bg-bgDark-300" />
                                        </div>
                                    </div>

                                    <div className="flex-1 flex flex-col p-5 gap-3 bg-white dark:bg-bgDark-840">
                                        <div className="flex-1 space-y-2.5">
                                            <div className="h-5 w-full rounded bg-gray-200 dark:bg-bgDark-300 animate-pulse" />
                                            <div className="h-5 w-4/5 rounded bg-gray-200 dark:bg-bgDark-300 animate-pulse" />

                                            <div className="pt-1.5 space-y-2">
                                                <div className="h-3.5 w-full rounded bg-gray-100 dark:bg-bgDark-300/50 animate-pulse" />
                                                <div className="h-3.5 w-11/12 rounded bg-gray-100 dark:bg-bgDark-300/50 animate-pulse" />
                                                <div className="h-3.5 w-2/3 rounded bg-gray-100 dark:bg-bgDark-300/50 animate-pulse" />
                                            </div>
                                        </div>

                                        <div className="mt-3">
                                            <div className="h-3 w-28 rounded bg-gray-100 dark:bg-bgDark-300/50 animate-pulse" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
            </section>
        </>
    );
}
